'use client';

import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { ApiInitiative } from '@/lib/api/types';
import { InitiativeForm } from './InitiativeForm';

interface NewInitiativeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (initiative: ApiInitiative) => void;
  /** When provided, the form will be prepopulated with values from this initiative (clone mode) */
  cloneFrom?: ApiInitiative;
  ownerId?: string;
  ownerType?: string;
  title?: string;
  defaultType?: string;
  fixedType?: string;
}

export function NewInitiativeDialog({
  isOpen,
  onClose,
  onSuccess,
  cloneFrom,
  ownerId,
  ownerType,
  title,
  defaultType,
  fixedType,
}: NewInitiativeDialogProps) {
  const [formKey, setFormKey] = useState(0);

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setFormKey((prev) => prev + 1);
        document.body.style.overflow = '';
        document.body.style.pointerEvents = '';
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Build initialData for clone mode: strip unique/system fields, reset status
  const initialData = cloneFrom
    ? ({
        ...cloneFrom,
        id: undefined,
        name: `${cloneFrom.name} (Copy)`,
        status: 'draft',
        visibility: 'private',
        isTemplate: false,
        createdAt: undefined,
        updatedAt: undefined,
      } as unknown as ApiInitiative)
    : undefined;

  const handleSuccess = (initiative: ApiInitiative) => {
    onSuccess?.(initiative);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[900px] flex flex-col max-h-[90vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-xl font-bold text-orange-500">
            {title || (cloneFrom ? `Clone: ${cloneFrom.name}` : 'Create Initiative')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <InitiativeForm
            key={formKey}
            initialData={initialData}
            ownerId={ownerId || cloneFrom?.ownerId || undefined}
            ownerType={ownerType || cloneFrom?.ownerType || 'entity'}
            calendarSupport="multiple"
            defaultType={defaultType || cloneFrom?.type}
            fixedType={fixedType}
            onSuccess={handleSuccess}
            onCancel={onClose}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
